import { useState, useCallback, useEffect } from "react";
import type { ColumnPreferences, GridColumnMetadata } from "../types";

const STORAGE_KEY_PREFIX = "column-prefs:";

interface UseColumnPreferencesOptions {
  resource: string;
  defaultColumns: GridColumnMetadata[];
  /** Max columns visible by default when no saved preferences exist. Excess columns (by sortOrder) are hidden. */
  maxDefaultVisible?: number;
}

interface UseColumnPreferencesResult {
  preferences: ColumnPreferences;
  visibleColumns: GridColumnMetadata[];
  isColumnVisible: (columnName: string) => boolean;
  toggleColumn: (columnName: string) => void;
  showColumn: (columnName: string) => void;
  hideColumn: (columnName: string) => void;
  setColumnOrder: (order: string[]) => void;
  setColumnWidth: (columnName: string, width: number) => void;
  resetToDefault: () => void;
}

const getDefaultPreferences = (): ColumnPreferences => ({
  hiddenColumns: [],
  columnOrder: [],
  columnWidths: {},
});

/** Compute default hidden columns when maxDefaultVisible is set */
const computeDefaultHidden = (
  columns: GridColumnMetadata[],
  maxVisible?: number
): string[] => {
  if (!maxVisible || columns.length <= maxVisible) return [];
  const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);
  return sorted.slice(maxVisible).map((col) => col.name);
};

export const useColumnPreferences = ({
  resource,
  defaultColumns,
  maxDefaultVisible,
}: UseColumnPreferencesOptions): UseColumnPreferencesResult => {
  const storageKey = `${STORAGE_KEY_PREFIX}${resource}`;

  // Initialize from localStorage or use defaults
  const [preferences, setPreferences] = useState<ColumnPreferences>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return JSON.parse(stored) as ColumnPreferences;
      }
    } catch (error) {
      console.error("Failed to parse column preferences:", error);
    }
    return getDefaultPreferences();
  });

  // Apply maxDefaultVisible when columns load and user hasn't explicitly configured columns
  const [hasAppliedDefaults, setHasAppliedDefaults] = useState(false);
  useEffect(() => {
    if (hasAppliedDefaults || !maxDefaultVisible || defaultColumns.length === 0)
      return;
    // Skip only if user has explicitly configured hidden columns
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ColumnPreferences;
        if (parsed.hiddenColumns && parsed.hiddenColumns.length > 0) {
          setHasAppliedDefaults(true);
          return;
        }
      } catch {
        /* ignore parse errors */
      }
    }
    const hiddenColumns = computeDefaultHidden(
      defaultColumns,
      maxDefaultVisible
    );
    if (hiddenColumns.length > 0) {
      setPreferences((prev) => ({ ...prev, hiddenColumns }));
    }
    setHasAppliedDefaults(true);
  }, [defaultColumns, maxDefaultVisible, storageKey, hasAppliedDefaults]);

  // Persist to localStorage when preferences change
  // Wait until defaults have been applied (if maxDefaultVisible is set) to avoid saving empty prefs
  useEffect(() => {
    if (maxDefaultVisible && !hasAppliedDefaults) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch (error) {
      console.error("Failed to save column preferences:", error);
    }
  }, [preferences, storageKey, maxDefaultVisible, hasAppliedDefaults]);

  // Check if a column is visible
  const isColumnVisible = useCallback(
    (columnName: string): boolean => {
      return !preferences.hiddenColumns.includes(columnName);
    },
    [preferences.hiddenColumns]
  );

  // Toggle column visibility
  const toggleColumn = useCallback((columnName: string) => {
    setPreferences((prev) => {
      const isHidden = prev.hiddenColumns.includes(columnName);
      return {
        ...prev,
        hiddenColumns: isHidden
          ? prev.hiddenColumns.filter((c) => c !== columnName)
          : [...prev.hiddenColumns, columnName],
      };
    });
  }, []);

  // Show a specific column
  const showColumn = useCallback((columnName: string) => {
    setPreferences((prev) => ({
      ...prev,
      hiddenColumns: prev.hiddenColumns.filter((c) => c !== columnName),
    }));
  }, []);

  // Hide a specific column
  const hideColumn = useCallback((columnName: string) => {
    setPreferences((prev) => ({
      ...prev,
      hiddenColumns: prev.hiddenColumns.includes(columnName)
        ? prev.hiddenColumns
        : [...prev.hiddenColumns, columnName],
    }));
  }, []);

  // Set column order
  const setColumnOrder = useCallback((order: string[]) => {
    setPreferences((prev) => ({
      ...prev,
      columnOrder: order,
    }));
  }, []);

  // Set column width
  const setColumnWidth = useCallback((columnName: string, width: number) => {
    setPreferences((prev) => ({
      ...prev,
      columnWidths: {
        ...prev.columnWidths,
        [columnName]: width,
      },
    }));
  }, []);

  // Reset to default (respects maxDefaultVisible)
  // Note: no need to clear localStorage here — the persist effect will
  // save the new preferences automatically after setPreferences updates state.
  const resetToDefault = useCallback(() => {
    const hiddenColumns = computeDefaultHidden(
      defaultColumns,
      maxDefaultVisible
    );
    setPreferences({ ...getDefaultPreferences(), hiddenColumns });
  }, [defaultColumns, maxDefaultVisible]);

  // Compute visible columns with ordering and width overrides
  const visibleColumns = defaultColumns
    .filter((col) => !preferences.hiddenColumns.includes(col.name))
    .map((col) => ({
      ...col,
      width: preferences.columnWidths[col.name] ?? col.width,
    }))
    .sort((a, b) => {
      const orderA = preferences.columnOrder.indexOf(a.name);
      const orderB = preferences.columnOrder.indexOf(b.name);
      // If not in order array, use original sortOrder
      if (orderA === -1 && orderB === -1) return a.sortOrder - b.sortOrder;
      if (orderA === -1) return 1;
      if (orderB === -1) return -1;
      return orderA - orderB;
    });

  return {
    preferences,
    visibleColumns,
    isColumnVisible,
    toggleColumn,
    showColumn,
    hideColumn,
    setColumnOrder,
    setColumnWidth,
    resetToDefault,
  };
};
